import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/safeDate';
import {
  useCaseServices, SERVICE_TYPES, SERVICE_STATUSES, caseServiceSchema,
} from '@/hooks/useCaseServices';

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const EMPTY_FORM = {
  service_type: '',
  description: '',
  service_date: '',
  due_date: '',
  status: 'pending',
  notes: '',
};

interface Props {
  caseId: string;
}

export default function CaseServicesTab({ caseId }: Props) {
  const {
    services, isLoading, error, createService, isCreating, updateService, deleteService,
  } = useCaseServices(caseId);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    const parsed = caseServiceSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? 'Preencha os campos obrigatórios');
      return;
    }
    try {
      await createService(parsed.data);
      toast.success('Serviço adicionado');
      setForm({ ...EMPTY_FORM });
      setShowForm(false);
    } catch {
      toast.error('Não foi possível salvar o serviço');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando serviços...
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center text-sm text-rose-600">
        Não foi possível carregar os serviços deste processo.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Serviços do Processo
        </h2>
        <Button size="sm" onClick={() => setShowForm((v) => !v)} className="rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Novo serviço
        </Button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">Serviço</label>
              <Select value={form.service_type} onValueChange={(v) => set('service_type', v)}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Data do serviço</label>
              <Input type="date" value={form.service_date} onChange={(e) => set('service_date', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Prazo</label>
              <Input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Observações</label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Detalhes do serviço"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} disabled={isCreating} className="rounded-full">
              {isCreating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Salvar
            </Button>
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Nenhum serviço registrado neste processo.
        </p>
      ) : (
        <div className="space-y-2">
          {services.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-foreground">{s.service_type}</p>
                  <Badge className={`border-0 text-[11px] ${STATUS_STYLES[s.status] ?? ''}`}>
                    {SERVICE_STATUSES.find((x) => x.value === s.status)?.label ?? s.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                  {s.service_date && <span>Data: {safeFormat(s.service_date, 'dd/MM/yyyy')}</span>}
                  {s.due_date && <span>Prazo: {safeFormat(s.due_date, 'dd/MM/yyyy')}</span>}
                  {s.assignee && (
                    <span>Responsável: {[s.assignee.first_name, s.assignee.last_name].filter(Boolean).join(' ')}</span>
                  )}
                </div>
                {s.notes && <p className="text-xs text-foreground/80 mt-2 whitespace-pre-wrap">{s.notes}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Select
                  value={s.status}
                  onValueChange={(v) => updateService({ id: s.id, updates: { status: v } })}
                >
                  <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_STATUSES.map((x) => (
                      <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                  onClick={() => deleteService(s.id)}
                  title="Remover serviço"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
